import React, { useState, useEffect } from 'react';
import OpenSeadragon from 'openseadragon';

interface ImageAdjustmentsProps {
  viewer: OpenSeadragon.Viewer | null;
}

interface AdjustmentValues {
  brightness: number;
  contrast: number;
  saturation: number;
  hue: number;
  gamma: number;
  invert: boolean;
  redChannel: number;
  greenChannel: number;
  blueChannel: number;
}

export function ImageAdjustments({ viewer }: ImageAdjustmentsProps) {
  const [adjustments, setAdjustments] = useState<AdjustmentValues>({
    brightness: 100,
    contrast: 100,
    saturation: 100,
    hue: 0,
    gamma: 1,
    invert: false,
    redChannel: 100,
    greenChannel: 100,
    blueChannel: 100
  });

  const [showHistogram, setShowHistogram] = useState(false);
  const [activeTab, setActiveTab] = useState<'basic' | 'advanced' | 'filters'>('basic');

  // Predefined filters for specific stains
  const presetFilters = [
    { name: 'Αρχικό', values: { brightness: 100, contrast: 100, saturation: 100, hue: 0, gamma: 1, invert: false } },
    { name: 'H&E Ενίσχυση', values: { brightness: 110, contrast: 115, saturation: 120, hue: 0, gamma: 1.1, invert: false } },
    { name: 'DAB Ενίσχυση', values: { brightness: 105, contrast: 120, saturation: 110, hue: -10, gamma: 1.0, invert: false } },
    { name: 'Αιματοξυλίνη', values: { brightness: 95, contrast: 110, saturation: 130, hue: 240, gamma: 1.0, invert: false } },
    { name: 'Ηωσίνη', values: { brightness: 105, contrast: 110, saturation: 125, hue: 340, gamma: 1.0, invert: false } },
    { name: 'Αρνητικό', values: { brightness: 100, contrast: 100, saturation: 100, hue: 0, gamma: 1, invert: true } },
    { name: 'Υψηλή Αντίθεση', values: { brightness: 100, contrast: 150, saturation: 100, hue: 0, gamma: 1.2, invert: false } },
    { name: 'Χαμηλό Φως', values: { brightness: 130, contrast: 90, saturation: 100, hue: 0, gamma: 0.8, invert: false } }
  ];

  // Apply adjustments to viewer
  useEffect(() => {
    if (!viewer) return;

    try {
      const canvas = viewer.canvas;
      if (!canvas) return;

      // Build CSS filter string
      const filters = [
        `brightness(${adjustments.brightness}%)`,
        `contrast(${adjustments.contrast}%)`,
        `saturate(${adjustments.saturation}%)`,
        `hue-rotate(${adjustments.hue}deg)`,
        adjustments.invert ? 'invert(1)' : ''
      ].filter(Boolean).join(' ');

      // Apply filters
      const viewerElement = viewer.container;
      if (viewerElement) {
        const canvasElement = viewerElement.querySelector('canvas');
        if (canvasElement) {
          canvasElement.style.filter = filters;
        }
      }

      // Apply RGB channel adjustments using CSS custom filters
      if (adjustments.redChannel !== 100 || adjustments.greenChannel !== 100 || adjustments.blueChannel !== 100) {
        const rgbMatrix = `
          ${adjustments.redChannel / 100} 0 0 0 0
          0 ${adjustments.greenChannel / 100} 0 0 0
          0 0 ${adjustments.blueChannel / 100} 0 0
          0 0 0 1 0
        `;
        
        // Note: SVG filters for RGB channels would require more complex implementation
        // For now, we'll use a simpler approach with CSS filters
      }

    } catch (error) {
      console.error('Failed to apply image adjustments:', error);
    }
  }, [viewer, adjustments]);

  // Update single adjustment
  const updateAdjustment = (key: keyof AdjustmentValues, value: number | boolean) => {
    setAdjustments(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Apply preset filter
  const applyPreset = (preset: typeof presetFilters[0]) => {
    setAdjustments(prev => ({
      ...prev,
      ...preset.values
    }));
  };

  // Reset all adjustments
  const resetAdjustments = () => {
    const defaultValues: AdjustmentValues = {
      brightness: 100,
      contrast: 100,
      saturation: 100,
      hue: 0,
      gamma: 1,
      invert: false,
      redChannel: 100,
      greenChannel: 100,
      blueChannel: 100
    };
    setAdjustments(defaultValues);
  };

  // Auto adjust (simple auto-levels)
  const autoAdjust = () => {
    // Simple auto adjustment algorithm
    setAdjustments({
      brightness: 105,
      contrast: 115,
      saturation: 110,
      hue: 0,
      gamma: 1.05,
      invert: false,
      redChannel: 100,
      greenChannel: 100,
      blueChannel: 100
    });
  };

  return (
    <div className="absolute bottom-20 left-4 z-20">
      <div className="bg-black bg-opacity-80 backdrop-blur-sm rounded-lg shadow-lg border border-white/20 p-3 w-80">
        <h3 className="text-white text-sm font-semibold mb-3">Ρυθμίσεις Εικόνας</h3>
        
        {/* Tabs */}
        <div className="flex space-x-1 mb-3">
          <button
            onClick={() => setActiveTab('basic')}
            className={`flex-1 px-2 py-1 text-xs font-medium rounded transition-colors ${
              activeTab === 'basic' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-700 hover:bg-gray-600 text-white'
            }`}
          >
            Βασικά
          </button>
          <button
            onClick={() => setActiveTab('advanced')}
            className={`flex-1 px-2 py-1 text-xs font-medium rounded transition-colors ${
              activeTab === 'advanced' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-700 hover:bg-gray-600 text-white'
            }`}
          >
            Προχωρημένα
          </button>
          <button
            onClick={() => setActiveTab('filters')}
            className={`flex-1 px-2 py-1 text-xs font-medium rounded transition-colors ${
              activeTab === 'filters' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-700 hover:bg-gray-600 text-white'
            }`}
          >
            Φίλτρα
          </button>
        </div>
        
        {/* Basic adjustments */}
        {activeTab === 'basic' && (
          <div className="space-y-3">
            {/* Brightness */}
            <div>
              <div className="flex justify-between items-center mb-1">
                <label className="text-xs text-gray-300">☀️ Φωτεινότητα</label>
                <span className="text-xs text-gray-400">{adjustments.brightness}%</span>
              </div>
              <input
                type="range"
                min="0"
                max="200"
                value={adjustments.brightness}
                onChange={(e) => updateAdjustment('brightness', parseInt(e.target.value))}
                className="w-full h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer"
              />
            </div>
            
            {/* Contrast */}
            <div>
              <div className="flex justify-between items-center mb-1">
                <label className="text-xs text-gray-300">◐ Αντίθεση</label>
                <span className="text-xs text-gray-400">{adjustments.contrast}%</span>
              </div>
              <input
                type="range"
                min="0"
                max="200"
                value={adjustments.contrast}
                onChange={(e) => updateAdjustment('contrast', parseInt(e.target.value))}
                className="w-full h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer"
              />
            </div>
            
            {/* Saturation */}
            <div>
              <div className="flex justify-between items-center mb-1">
                <label className="text-xs text-gray-300">🎨 Κορεσμός</label>
                <span className="text-xs text-gray-400">{adjustments.saturation}%</span>
              </div>
              <input
                type="range"
                min="0"
                max="200"
                value={adjustments.saturation}
                onChange={(e) => updateAdjustment('saturation', parseInt(e.target.value))}
                className="w-full h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer"
              />
            </div>
            
            {/* Quick actions */}
            <div className="flex space-x-1 pt-2">
              <button
                onClick={autoAdjust}
                className="flex-1 px-2 py-1 text-xs bg-blue-700 hover:bg-blue-600 text-white rounded"
              >
                🔄 Αυτόματη
              </button>
              <button
                onClick={resetAdjustments}
                className="flex-1 px-2 py-1 text-xs bg-gray-700 hover:bg-gray-600 text-white rounded"
              >
                ↺ Επαναφορά
              </button>
              <button
                onClick={() => updateAdjustment('invert', !adjustments.invert)}
                className={`flex-1 px-2 py-1 text-xs rounded ${
                  adjustments.invert 
                    ? 'bg-purple-600 hover:bg-purple-500 text-white' 
                    : 'bg-gray-700 hover:bg-gray-600 text-white'
                }`}
              >
                ⇄ Αντιστροφή
              </button>
            </div>
          </div>
        )}
        
        {/* Advanced adjustments */}
        {activeTab === 'advanced' && (
          <div className="space-y-3">
            {/* Hue */}
            <div>
              <div className="flex justify-between items-center mb-1">
                <label className="text-xs text-gray-300">🌈 Απόχρωση</label>
                <span className="text-xs text-gray-400">{adjustments.hue}°</span>
              </div>
              <input
                type="range"
                min="-180"
                max="180"
                value={adjustments.hue}
                onChange={(e) => updateAdjustment('hue', parseInt(e.target.value))}
                className="w-full h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer"
              />
            </div>
            
            {/* Gamma */}
            <div>
              <div className="flex justify-between items-center mb-1">
                <label className="text-xs text-gray-300">γ Gamma</label>
                <span className="text-xs text-gray-400">{adjustments.gamma.toFixed(2)}</span>
              </div>
              <input
                type="range"
                min="0.1"
                max="3"
                step="0.1"
                value={adjustments.gamma}
                onChange={(e) => updateAdjustment('gamma', parseFloat(e.target.value))}
                className="w-full h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer"
              />
            </div>
            
            {/* RGB Channels */}
            <div className="border-t border-white/20 pt-2">
              <p className="text-xs text-gray-400 mb-2">Κανάλια RGB:</p>
              
              {/* Red */}
              <div className="mb-2">
                <div className="flex justify-between items-center mb-1">
                  <label className="text-xs text-red-400">Κόκκινο</label>
                  <span className="text-xs text-gray-400">{adjustments.redChannel}%</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="200"
                  value={adjustments.redChannel}
                  onChange={(e) => updateAdjustment('redChannel', parseInt(e.target.value))}
                  className="w-full h-1 bg-red-900 rounded-lg appearance-none cursor-pointer"
                />
              </div>
              
              {/* Green */}
              <div className="mb-2">
                <div className="flex justify-between items-center mb-1">
                  <label className="text-xs text-green-400">Πράσινο</label>
                  <span className="text-xs text-gray-400">{adjustments.greenChannel}%</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="200"
                  value={adjustments.greenChannel}
                  onChange={(e) => updateAdjustment('greenChannel', parseInt(e.target.value))}
                  className="w-full h-1 bg-green-900 rounded-lg appearance-none cursor-pointer"
                />
              </div>
              
              {/* Blue */}
              <div>
                <div className="flex justify-between items-center mb-1">
                  <label className="text-xs text-blue-400">Μπλε</label>
                  <span className="text-xs text-gray-400">{adjustments.blueChannel}%</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="200"
                  value={adjustments.blueChannel}
                  onChange={(e) => updateAdjustment('blueChannel', parseInt(e.target.value))}
                  className="w-full h-1 bg-blue-900 rounded-lg appearance-none cursor-pointer"
                />
              </div>
            </div>
          </div>
        )}
        
        {/* Preset filters */}
        {activeTab === 'filters' && (
          <div>
            <p className="text-xs text-gray-400 mb-2">Προκαθορισμένα φίλτρα:</p>
            <div className="grid grid-cols-2 gap-1">
              {presetFilters.map((preset) => (
                <button
                  key={preset.name}
                  onClick={() => applyPreset(preset)}
                  className="px-2 py-2 text-xs bg-gray-700 hover:bg-gray-600 text-white rounded transition-colors"
                >
                  {preset.name}
                </button>
              ))}
            </div>
            
            {/* Histogram toggle */}
            <div className="mt-3 pt-3 border-t border-white/20">
              <button
                onClick={() => setShowHistogram(!showHistogram)}
                className="w-full px-3 py-2 text-xs bg-gray-700 hover:bg-gray-600 text-white rounded"
              >
                📊 {showHistogram ? 'Απόκρυψη' : 'Εμφάνιση'} Ιστογράμματος
              </button>
            </div>
            
            {/* Simple histogram visualization */}
            {showHistogram && (
              <div className="mt-2 p-2 bg-gray-900 rounded">
                <p className="text-xs text-gray-400 mb-1">Κατανομή Τόνων</p>
                <div className="h-16 bg-gray-800 rounded flex items-end space-x-1 p-1">
                  {[...Array(16)].map((_, i) => (
                    <div
                      key={i}
                      className="flex-1 bg-gradient-to-t from-blue-500 to-blue-300"
                      style={{ height: `${Math.random() * 100}%` }}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Current values summary */}
        <div className="mt-3 pt-2 border-t border-white/20">
          <p className="text-xs text-gray-500">
            Τρέχουσες τιμές: B:{adjustments.brightness} C:{adjustments.contrast} S:{adjustments.saturation}
          </p>
        </div>
      </div>
    </div>
  );
}